import React from 'react';
import classNames from 'classnames';

import Field, { hasDataInFields } from '../../Field';
import SectionContainer from '../SectionContainer';
import landingPageIcons from '../landingPageIcons';

import css from './SectionCardGrid.module.css';

/**
 * @typedef {Object} CardGridBlock
 * @property {string} blockId
 * @property {keyof landingPageIcons?} icon glyph shown in the card's icon box. Omit to render no icon box.
 * @property {string?} iconBg background color (hex) for the icon box
 * @property {Object} title card title (Field data, e.g. heading3)
 * @property {Object} text card description (Field data, e.g. paragraph)
 * @property {Array<string>?} items checklist rendered below the description
 * @property {keyof landingPageIcons?} itemIcon glyph used for each checklist row (defaults to 'checkGreen')
 * @property {Object?} callToAction optional per-card button (Field data). Only rendered when cardVariant is 'accent'.
 * @property {'solid' | 'outline'} ctaVariant visual style for callToAction (defaults to 'solid')
 * @property {'brand' | 'neutral'} tone card fill when cardVariant is 'accent' - 'brand' is the
 *   solid green card (light text), 'neutral' is the soft grey card (dark text). Defaults to 'neutral'.
 */

/**
 * Section that lays out a header followed by a responsive grid of
 * title+description+checklist cards. Two visual variants:
 * - 'plain': white card, neutral border, no per-card CTA, centered header (e.g. "Why choose us")
 * - 'accent': solid-filled card (see block `tone`) with a CTA button, and a split header where
 *   the eyebrow+title sit left and the description sits right (e.g. "Built for our community")
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className
 * @param {string?} props.rootClassName
 * @param {string} props.sectionId
 * @param {'cardGrid'} props.sectionType
 * @param {Object?} props.eyebrow small label above the title, 'accent' variant only (Field data)
 * @param {Object?} props.title header title (Field data)
 * @param {Object?} props.description header description (Field data)
 * @param {2 | 3} props.numColumns
 * @param {'plain' | 'accent'} props.cardVariant
 * @param {Array<CardGridBlock>} props.blocks
 * @param {Object?} props.appearance
 * @param {Object} props.options
 * @returns {JSX.Element}
 */
const SectionCardGrid = props => {
  const {
    sectionId,
    className,
    rootClassName,
    eyebrow,
    title,
    description,
    numColumns = 3,
    cardVariant = 'plain',
    blocks = [],
    appearance,
    options,
  } = props;

  const fieldOptions = { fieldComponents: options?.fieldComponents };
  const hasHeader = hasDataInFields([eyebrow, title, description], fieldOptions);
  const isAccent = cardVariant === 'accent';

  return (
    <SectionContainer
      id={sectionId}
      className={classNames(rootClassName || css.root, className)}
      appearance={appearance}
      options={fieldOptions}
    >
      {hasHeader && isAccent ? (
        // Split header: eyebrow + title on the left, description on the right. Collapses to a
        // single stacked column below --viewportLarge (see .headerSplit).
        <header className={css.headerSplit}>
          <div className={css.headerLead}>
            <Field data={eyebrow} className={css.headerEyebrow} options={fieldOptions} />
            <Field data={title} className={css.headerTitle} options={fieldOptions} />
          </div>
          <Field data={description} className={css.headerDescription} options={fieldOptions} />
        </header>
      ) : hasHeader ? (
        <header className={css.headerCentered}>
          <Field data={title} className={css.headerTitle} options={fieldOptions} />
          <Field data={description} className={css.headerDescription} options={fieldOptions} />
        </header>
      ) : null}

      <div
        className={classNames(css.grid, {
          [css.gridAccent]: isAccent,
          [css.gridTwoColumns]: numColumns === 2,
          [css.gridThreeColumns]: numColumns === 3,
        })}
      >
        {blocks.map((block, index) => {
          const {
            blockId,
            icon,
            iconBg,
            title: cardTitle,
            text,
            items = [],
            itemIcon = 'checkGreen',
            callToAction,
            ctaVariant = 'solid',
            tone = 'neutral',
          } = block;

          const iconSrc = icon && landingPageIcons[icon];
          const itemIconSrc = landingPageIcons[itemIcon] || landingPageIcons.checkGreen;

          return (
            <div
              key={blockId || `${sectionId}-card-${index}`}
              className={classNames(css.card, {
                [css.cardAccent]: isAccent,
                [css.cardBrand]: isAccent && tone === 'brand',
                [css.cardNeutral]: isAccent && tone === 'neutral',
              })}
            >
              {iconSrc ? (
                <div className={css.iconBox} style={{ backgroundColor: iconBg }}>
                  <img className={css.icon} src={iconSrc} alt="" />
                </div>
              ) : null}

              <Field
                data={cardTitle}
                className={classNames(css.cardTitle, { [css.cardTitleLarge]: isAccent })}
                options={fieldOptions}
              />
              <Field data={text} className={css.cardText} options={fieldOptions} />

              {items.length > 0 ? (
                <ul className={css.checklist}>
                  {items.map((item, itemIndex) => (
                    <li key={itemIndex} className={css.checklistItem}>
                      <img className={css.checklistIcon} src={itemIconSrc} alt="" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              ) : null}

              {isAccent ? (
                <Field
                  data={callToAction}
                  className={classNames(css.cardCta, {
                    [css.cardCtaSolid]: ctaVariant === 'solid',
                    [css.cardCtaOutline]: ctaVariant === 'outline',
                  })}
                  options={fieldOptions}
                />
              ) : null}
            </div>
          );
        })}
      </div>
    </SectionContainer>
  );
};

export default SectionCardGrid;
